import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Home, 
  Gamepad2, 
  Users, 
  User, 
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Monitor,
  ChevronLeft,
  ChevronRight,
  Compass,
  BarChart3,
  DollarSign,
  Check,
  Search,
  Headphones,
} from 'lucide-react'
import { useThemeStore } from '@/store/useThemeStore'
import beemiLogo from '@/assets/icons/beemi-logo.png'
import { LINE_GRADIENT } from '@/constants/gradients'
import ToastContainer from '../common/ToastContainer'

interface LayoutProps {
  children: ReactNode
  onLogout: () => void
  user?: {
    id: string
    username: string
    display_name: string
    primary_email: string
    image_url?: string
  } | null
}

const Layout = ({ children, onLogout, user }: LayoutProps) => {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(false)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const { mode, setThemeMode } = useThemeStore()

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024)
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Games', href: '/projects', icon: Gamepad2 },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'Revenue', href: '/revenue', icon: DollarSign },
    { name: 'Community', href: '/community', icon: Compass },
    { name: 'Teams', href: '/teams', icon: Users },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Support', href: '/support', icon: Headphones },
  ]

  const isActive = (path: string) => location.pathname === path

  // Get search placeholder based on current page
  const getSearchPlaceholder = () => {
    if (location.pathname === '/projects') return 'Search games...'
    if (location.pathname === '/teams') return 'Search teams...'
    if (location.pathname === '/community') return 'Search community...'
    return 'Search games, teams, creators...'
  }

  // Calculate sidebar width based on screen size and collapse state
  const sidebarWidth = isLargeScreen ? (sidebarCollapsed ? 64 : 256) : 256

  return (
    // Make the outer wrapper exactly viewport height and a flex container
    <div className="h-screen flex bg-gray-50 dark:bg-dark-bg">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <motion.div
        initial={{ x: isLargeScreen ? 0 : -280 }}
        animate={{ 
          x: isLargeScreen ? 0 : (sidebarOpen ? 0 : -280),
          width: sidebarWidth 
        }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`
          ${isLargeScreen ? 'sticky top-0' : 'fixed'}
          inset-y-0 left-0 z-50
          bg-white dark:bg-dark-surface
          border-r border-gray-200 dark:border-dark-border
          flex flex-col
          h-full              // fill 100% of parent (h-screen)
        `}
        style={{ width: sidebarWidth }}
      >
        {/* Logo */}
        <div className={`flex items-center ${sidebarCollapsed && isLargeScreen ? 'justify-center' : 'justify-between'} ${sidebarCollapsed && isLargeScreen ? 'px-4' : 'px-6'} py-4 border-b border-gray-200 dark:border-dark-border h-17`}>
          <Link to="/" className="group flex items-center gap-2 text-xl">
            <img src={beemiLogo} alt="Beemi" className="w-8 h-8 flex-shrink-0" />
            {(!isLargeScreen || !sidebarCollapsed) && (
              <span className="whitespace-nowrap flex items-center">
                <span className="relative inline-block">
                  {/* Solid color layer */}
                  <span className="font-bold lowercase text-2xl text-gray-900 dark:text-dark-text transition-opacity duration-300 group-hover:opacity-0">
                    Beemi
                  </span>
                  {/* Gradient layer */}
                  <span 
                    className="font-bold lowercase text-2xl absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      backgroundImage: LINE_GRADIENT,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Beemi
                  </span>
                </span>
                <span className="font-light uppercase text-gray-900 dark:text-dark-text transition-colors duration-300 group-hover:text-[#FF00CF]">Studio</span>
              </span>
            )}
          </Link>
          {!isLargeScreen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => !isLargeScreen && setSidebarOpen(false)}
                className={`
                  flex items-center ${sidebarCollapsed && isLargeScreen ? 'justify-center' : 'space-x-3'} ${sidebarCollapsed && isLargeScreen ? 'px-0' : 'px-3'} py-2 rounded-lg text-base font-medium transition-all duration-200 relative
                  ${isActive(item.href)
                    ? (sidebarCollapsed && isLargeScreen 
                        ? 'bg-transparent' 
                        : 'bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/10 dark:to-pink-900/10')
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                  }
                `}
                style={isActive(item.href) ? {
                  borderLeft: '3px solid',
                  borderImage: 'linear-gradient(180deg, rgb(252, 131, 2) 0%, #FF00CF 100%) 1',
                } : undefined}
                title={sidebarCollapsed && isLargeScreen ? item.name : undefined}
              >
                <Icon 
                  className="w-5 h-5 flex-shrink-0" 
                  style={isActive(item.href) ? { color: '#FF00CF' } : undefined}
                />
                {(!sidebarCollapsed || !isLargeScreen) && (
                  <span 
                    style={isActive(item.href) ? {
                      backgroundImage: LINE_GRADIENT,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    } : undefined}
                  >
                    {item.name}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Bottom section - User dropdown and Collapse button */}
        <div className="flex-shrink-0 p-4 space-y-2">
          {/* User section with dropdown */}
          <div className="relative">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className={`
                flex items-center w-full rounded-lg transition-colors
                ${sidebarCollapsed && isLargeScreen ? 'justify-center p-2' : 'space-x-3 px-3 py-2'}
                hover:bg-gray-100 dark:hover:bg-gray-800
              `}
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                {user?.image_url ? (
                  <img 
                    src={user.image_url} 
                    alt={user.display_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-primary-700">
                    {user?.display_name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              {(!sidebarCollapsed || !isLargeScreen) && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-text truncate">
                    {user ? (user.display_name || user.username) : 'Loading...'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted truncate">
                    {user ? user.primary_email : 'Loading...'}
                  </p>
                </div>
              )}
            </button>
            
            {/* Dropdown menu */}
            {userDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setUserDropdownOpen(false)}
                />
                <div 
                  className={`
                    absolute ${sidebarCollapsed && isLargeScreen ? 'left-full ml-2 bottom-0' : 'bottom-full mb-2 left-0 right-0'}
                    bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-50 overflow-hidden min-w-[200px]
                  `}
                >
                  {/* Theme options */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-dark-border">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Theme
                    </p>
                    <div className="space-y-1">
                      <button
                        onClick={() => {
                          setThemeMode('light')
                        }}
                        className={`
                          flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors
                          ${mode === 'light' ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          <Sun className="w-4 h-4" />
                          <span>Light</span>
                        </div>
                        {mode === 'light' && <Check className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => {
                          setThemeMode('dark')
                        }}
                        className={`
                          flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors
                          ${mode === 'dark' ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          <Moon className="w-4 h-4" />
                          <span>Dark</span>
                        </div>
                        {mode === 'dark' && <Check className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => {
                          setThemeMode('system')
                        }}
                        className={`
                          flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors
                          ${mode === 'system' ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          <Monitor className="w-4 h-4" />
                          <span>System</span>
                        </div>
                        {mode === 'system' && <Check className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Sign out */}
                  <button
                    onClick={() => {
                      setUserDropdownOpen(false)
                      onLogout()
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </>
            )}
          </div>
          
          {/* Expand/Collapse button */}
          {isLargeScreen && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`
                flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2'} w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 rounded-lg transition-colors
              `}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4 flex-shrink-0" /> : <ChevronLeft className="w-4 h-4 flex-shrink-0" />}
              {!sidebarCollapsed && <span>{sidebarCollapsed ? 'Expand' : 'Collapse'}</span>}
            </button>
          )}
        </div>
      </motion.div>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-full">
        {/* Header */}
        <header className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {!isLargeScreen && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white flex-shrink-0"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}
          
            {/* Search bar - centered */}
            <div className="flex-1 max-w-2xl mx-auto">
              <div className="relative group">
                {/* Gradient border wrapper - visible on focus */}
                <div 
                  className="absolute -inset-[2px] rounded-[10px] opacity-0 group-focus-within:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: LINE_GRADIENT }}
                />
                <div className="relative flex items-center">
                  <Search className="absolute left-3 w-5 h-5 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                  <input
                    type="search"
                    placeholder={getSearchPlaceholder()}
                    className="relative w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-dark-bg border-2 border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 transition-all focus:outline-none focus:ring-0 focus:shadow-none"
                    style={{ outline: 'none', boxShadow: 'none' }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'transparent'
                      e.currentTarget.style.outline = 'none'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = ''
                    }}
                  />
                </div>
              </div>
            </div>
           
            <div className="flex items-center space-x-4 flex-shrink-0">
              {/* Empty space for future additions */}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Global Toast Notifications */}
      <ToastContainer />

    </div>
  )
}

export default Layout
